import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Coins, Zap, Star, Crown, Gem, Sparkles, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';
import { trackCTAClick } from '@/lib/metaPixel';

const creditPacks = [
  { name: 'Starter', credits: 50, price: 2.99, icon: Coins },
  { name: 'Growth', credits: 200, price: 9.99, icon: Zap, popular: true },
  { name: 'Pro', credits: 500, price: 22.99, icon: Star },
  { name: 'Elite', credits: 2000, price: 69.99, icon: Crown },
  { name: 'Ultimate', credits: 35000, price: 499, icon: Gem },
];

export const AdditionalPricingBox = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [creditsOpen, setCreditsOpen] = useState(false);

  return (
    <section ref={ref} className="bg-background px-4 py-12 sm:py-16">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Launch Kit - Always visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="relative rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/5 via-card to-card p-6 sm:p-8 shadow-lg overflow-hidden"
        >
          <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-primary-foreground text-[10px] sm:text-xs font-bold rounded-bl-xl">
            <TranslatedText>PREMIUM</TranslatedText>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold">
              <TranslatedText>AI Business Launch Kit</TranslatedText>
            </h4>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            <TranslatedText>
              A fully personalized strategic blueprint, prepared for you and delivered within 14 days.
            </TranslatedText>
          </p>

          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              <TranslatedText>From</TranslatedText>
            </span>
            <span className="text-4xl sm:text-5xl font-bold text-foreground">$1,799</span>
            <span className="text-sm text-muted-foreground">
              <TranslatedText>one-time</TranslatedText>
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-5 italic">
            <TranslatedText>
              Final price varies based on project complexity, business model difficulty, and time required to build your custom blueprint.
            </TranslatedText>
          </p>

          <ul className="space-y-2.5 mb-6">
            {[
              'Personalized business strategy & niche selection',
              'Custom monetization roadmap',
              'AI tool stack tailored to your project',
              'Step-by-step 90-day execution plan',
              'Delivered as a premium PDF guide in 14 days',
              'Custom quote provided after intake review',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-foreground/90">
                  <TranslatedText>{item}</TranslatedText>
                </span>
              </li>
            ))}
          </ul>

          <Button
            onClick={() => {
              trackCTAClick('Learn About Launch Kit', 'Pricing-LaunchKit');
              window.location.href = '/launch-kit';
            }}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 rounded-xl group"
          >
            <TranslatedText>Learn More About the Launch Kit</TranslatedText>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>

          <p className="text-xs text-muted-foreground mt-3 text-center">
            <TranslatedText>For founders ready to skip the guesswork.</TranslatedText>
          </p>
        </motion.div>

        {/* Credit Packs - Collapsible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl border border-border bg-card overflow-hidden"
        >
          <button
            onClick={() => setCreditsOpen(!creditsOpen)}
            className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-foreground">
                  <TranslatedText>View AI Credit Pack Pricing</TranslatedText>
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  <TranslatedText>Optional top-ups for AI tools usage</TranslatedText>
                </p>
              </div>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${
                creditsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          <AnimatePresence initial={false}>
            {creditsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-5 sm:px-6 pb-6 pt-2 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-4 pt-4">
                    <TranslatedText>
                      Top up anytime. Use credits for scripts, captions, images, niches, and content plans.
                    </TranslatedText>
                  </p>
                  <div className="space-y-2">
                    {creditPacks.map((pack) => {
                      const Icon = pack.icon;
                      return (
                        <div
                          key={pack.name}
                          className={`flex items-center justify-between p-3 sm:p-4 rounded-xl border transition-colors ${
                            pack.popular
                              ? 'border-primary/40 bg-primary/5'
                              : 'border-border bg-background hover:border-primary/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4 text-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm sm:text-base">{pack.name}</span>
                                {pack.popular && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary text-primary-foreground">
                                    POPULAR
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {pack.credits.toLocaleString()} <TranslatedText>credits</TranslatedText>
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-sm sm:text-base">${pack.price}</div>
                            <div className="text-[10px] text-muted-foreground">
                              ${(pack.price / pack.credits).toFixed(4)}/cr
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    <TranslatedText>Available inside the member dashboard after signup.</TranslatedText>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default AdditionalPricingBox;
